import { supabase } from '../supabaseClient';
import { API_BASE_URL } from '../config/api';

export interface Company {
    id?: string;
    name: string;
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    phone?: string;
    email?: string;
    contactPerson?: string;
    isCarrier: boolean;
    isOutlet: boolean;
    isSupplier: boolean;
    isBillTo: boolean;
    notes?: string;
    isActive: boolean;
    createdAt?: string;
    updatedAt?: string;
    accountId?: string;
    alias?: string;
}

export async function getAllCompanies(impersonationData?: {
    isImpersonating: boolean;
    impersonatedUserId: string;
    accountId: string;
}): Promise<Company[]> {
    try {
        let companiesData: any[] = [];

        if (impersonationData?.isImpersonating) {
            // Use impersonation endpoint for companies
            const { data: { user: currentUser } } = await supabase.auth.getUser();
            const token = (await supabase.auth.getSession()).data.session?.access_token;

            const response = await fetch(`${API_BASE_URL}/admin/impersonate/${impersonationData.impersonatedUserId}/account/${impersonationData.accountId}/companies?currentUserId=${currentUser?.id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch impersonated companies');
            }

            const responseData = await response.json();
            companiesData = responseData.companies || [];
        } else {
            // Normal companies fetching
            const { data, error } = await supabase
                .from('companies')
                .select('*')
                .eq('is_active', true)
                .order('name', { ascending: true });

            if (error) {
                throw new Error(error.message);
            }

            companiesData = data || [];
        }

        return companiesData.map((company: any) => ({
            id: company.id,
            name: company.name,
            address: company.address,
            city: company.city,
            state: company.state,
            zipCode: company.zip_code,
            phone: company.phone,
            email: company.email,
            contactPerson: company.contact_person,
            isCarrier: company.is_carrier,
            isOutlet: company.is_outlet,
            isSupplier: company.is_supplier,
            isBillTo: company.is_bill_to,
            notes: company.notes,
            isActive: company.is_active,
            createdAt: company.created_at,
            updatedAt: company.updated_at,
            accountId: company.account_id,
            alias: company.alias,
        }));
    } catch (error: any) {
        throw new Error(error.message || 'Failed to fetch companies');
    }
}

export async function getCompaniesByType(
    type: 'carrier' | 'outlet' | 'supplier' | 'billto',
    impersonationData?: {
        isImpersonating: boolean;
        impersonatedUserId: string;
        accountId: string;
    }
): Promise<Company[]> {
    try {
        let companiesData: any[] = [];

        if (impersonationData?.isImpersonating) {
            // Use impersonation endpoint for companies by type
            const { data: { user: currentUser } } = await supabase.auth.getUser();
            const token = (await supabase.auth.getSession()).data.session?.access_token;

            const response = await fetch(`${API_BASE_URL}/admin/impersonate/${impersonationData.impersonatedUserId}/account/${impersonationData.accountId}/companies?type=${type}&currentUserId=${currentUser?.id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch impersonated companies by type');
            }

            const responseData = await response.json();
            companiesData = responseData.companies || [];
        } else {
            // Normal companies fetching by type
            const columnMap = {
                carrier: 'is_carrier',
                outlet: 'is_outlet',
                supplier: 'is_supplier',
                billto: 'is_bill_to'
            };

            const { data, error } = await supabase
                .from('companies')
                .select('*')
                .eq(columnMap[type], true)
                .eq('is_active', true)
                .order('name', { ascending: true });

            if (error) {
                throw new Error(error.message);
            }

            companiesData = data || [];
        }

        return companiesData.map((company: any) => ({
            id: company.id,
            name: company.name,
            address: company.address,
            city: company.city,
            state: company.state,
            zipCode: company.zip_code,
            phone: company.phone,
            email: company.email,
            contactPerson: company.contact_person,
            isCarrier: company.is_carrier,
            isOutlet: company.is_outlet,
            isSupplier: company.is_supplier,
            isBillTo: company.is_bill_to,
            notes: company.notes,
            isActive: company.is_active,
            createdAt: company.created_at,
            updatedAt: company.updated_at,
            accountId: company.account_id,
            alias: company.alias,
        }));
    } catch (error: any) {
        throw new Error(error.message || 'Failed to fetch companies by type');
    }
}

export async function createCompany(
    companyData: Omit<Company, 'id' | 'createdAt' | 'updatedAt'>,
    accountId: string,
    impersonationData?: {
        isImpersonating: boolean;
        impersonatedUserId: string;
    }
): Promise<Company> {
    try {
        if (impersonationData?.isImpersonating) {
            // Use impersonation endpoint for company creation
            const { data: { user: currentUser } } = await supabase.auth.getUser();
            const token = (await supabase.auth.getSession()).data.session?.access_token;

            // Transform frontend Company interface to backend format
            const backendCompanyData = {
                name: companyData.name,
                address: companyData.address,
                city: companyData.city,
                state: companyData.state,
                zip_code: companyData.zipCode,
                phone: companyData.phone,
                email: companyData.email,
                contact_person: companyData.contactPerson,
                is_carrier: companyData.isCarrier,
                is_outlet: companyData.isOutlet,
                is_supplier: companyData.isSupplier,
                is_bill_to: companyData.isBillTo,
                notes: companyData.notes,
                is_active: companyData.isActive,
                alias: companyData.alias,
            };

            const response = await fetch(`${API_BASE_URL}/admin/impersonate/${impersonationData.impersonatedUserId}/account/${accountId}/companies`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    currentUserId: currentUser?.id,
                    companyData: backendCompanyData
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to create company for impersonated user');
            }

            const responseData = await response.json();
            const data = responseData.company;

            // Transform backend response to frontend format
            return {
                id: data.id,
                name: data.name,
                address: data.address,
                city: data.city,
                state: data.state,
                zipCode: data.zip_code,
                phone: data.phone,
                email: data.email,
                contactPerson: data.contact_person,
                isCarrier: data.is_carrier,
                isOutlet: data.is_outlet,
                isSupplier: data.is_supplier,
                isBillTo: data.is_bill_to,
                notes: data.notes,
                isActive: data.is_active,
                createdAt: data.created_at,
                updatedAt: data.updated_at,
                accountId: data.account_id,
                alias: data.alias,
            };
        }

        // Normal company creation with existing logic
        const now = new Date().toISOString();

        // First check if an inactive company with this name exists in the same account
        const { data: existingCompany } = await supabase
            .from('companies')
            .select('*')
            .eq('name', companyData.name)
            .eq('account_id', accountId)
            .eq('is_active', false)
            .single();

        if (existingCompany) {
            // Reactivate and update the existing company
            const { data, error } = await supabase
                .from('companies')
                .update({
                    address: companyData.address,
                    city: companyData.city,
                    state: companyData.state,
                    zip_code: companyData.zipCode,
                    phone: companyData.phone,
                    email: companyData.email,
                    contact_person: companyData.contactPerson,
                    is_carrier: companyData.isCarrier,
                    is_outlet: companyData.isOutlet,
                    is_supplier: companyData.isSupplier,
                    is_bill_to: companyData.isBillTo,
                    notes: companyData.notes,
                    is_active: true,
                    updated_at: now,
                    alias: companyData.alias,
                })
                .eq('id', existingCompany.id)
                .select()
                .single();

            if (error) {
                throw new Error(error.message);
            }

            return {
                id: data.id,
                name: data.name,
                address: data.address,
                city: data.city,
                state: data.state,
                zipCode: data.zip_code,
                phone: data.phone,
                email: data.email,
                contactPerson: data.contact_person,
                isCarrier: data.is_carrier,
                isOutlet: data.is_outlet,
                isSupplier: data.is_supplier,
                isBillTo: data.is_bill_to,
                notes: data.notes,
                isActive: data.is_active,
                createdAt: data.created_at,
                updatedAt: data.updated_at,
                accountId: data.account_id,
                alias: data.alias,
            };
        }

        // Create new company if no inactive one exists
        const { data, error } = await supabase
            .from('companies')
            .insert([
                {
                    name: companyData.name,
                    address: companyData.address,
                    city: companyData.city,
                    state: companyData.state,
                    zip_code: companyData.zipCode,
                    phone: companyData.phone,
                    email: companyData.email,
                    contact_person: companyData.contactPerson,
                    is_carrier: companyData.isCarrier,
                    is_outlet: companyData.isOutlet,
                    is_supplier: companyData.isSupplier,
                    is_bill_to: companyData.isBillTo,
                    notes: companyData.notes,
                    is_active: companyData.isActive,
                    account_id: accountId,
                    created_at: now,
                    updated_at: now,
                    alias: companyData.alias,
                }
            ])
            .select()
            .single();

        if (error) {
            throw new Error(error.message);
        }

        return {
            id: data.id,
            name: data.name,
            address: data.address,
            city: data.city,
            state: data.state,
            zipCode: data.zip_code,
            phone: data.phone,
            email: data.email,
            contactPerson: data.contact_person,
            isCarrier: data.is_carrier,
            isOutlet: data.is_outlet,
            isSupplier: data.is_supplier,
            isBillTo: data.is_bill_to,
            notes: data.notes,
            isActive: data.is_active,
            createdAt: data.created_at,
            updatedAt: data.updated_at,
            accountId: data.account_id,
        };
    } catch (error: any) {
        throw new Error(error.message || 'Failed to create company');
    }
}

export async function updateCompany(
    id: string,
    updates: Partial<Company>,
    impersonationData?: {
        isImpersonating: boolean;
        impersonatedUserId: string;
        accountId: string;
    }
): Promise<Company> {
    try {
        if (impersonationData?.isImpersonating) {
            // Use impersonation endpoint for company update
            const { data: { user: currentUser } } = await supabase.auth.getUser();
            const token = (await supabase.auth.getSession()).data.session?.access_token;

            // Transform frontend updates to backend format
            const backendUpdates: any = {};
            if (updates.name !== undefined) backendUpdates.name = updates.name;
            if (updates.address !== undefined) backendUpdates.address = updates.address;
            if (updates.city !== undefined) backendUpdates.city = updates.city;
            if (updates.state !== undefined) backendUpdates.state = updates.state;
            if (updates.zipCode !== undefined) backendUpdates.zip_code = updates.zipCode;
            if (updates.phone !== undefined) backendUpdates.phone = updates.phone;
            if (updates.email !== undefined) backendUpdates.email = updates.email;
            if (updates.contactPerson !== undefined) backendUpdates.contact_person = updates.contactPerson;
            if (updates.isCarrier !== undefined) backendUpdates.is_carrier = updates.isCarrier;
            if (updates.isOutlet !== undefined) backendUpdates.is_outlet = updates.isOutlet;
            if (updates.isSupplier !== undefined) backendUpdates.is_supplier = updates.isSupplier;
            if (updates.isBillTo !== undefined) backendUpdates.is_bill_to = updates.isBillTo;
            if (updates.notes !== undefined) backendUpdates.notes = updates.notes;
            if (updates.isActive !== undefined) backendUpdates.is_active = updates.isActive;
            if (updates.alias !== undefined) backendUpdates.alias = updates.alias;

            const response = await fetch(`${API_BASE_URL}/admin/impersonate/${impersonationData.impersonatedUserId}/account/${impersonationData.accountId}/companies/${id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    currentUserId: currentUser?.id,
                    updates: backendUpdates
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update company for impersonated user');
            }

            const responseData = await response.json();
            const data = responseData.company;

            // Transform backend response to frontend format
            return {
                id: data.id,
                name: data.name,
                address: data.address,
                city: data.city,
                state: data.state,
                zipCode: data.zip_code,
                phone: data.phone,
                email: data.email,
                contactPerson: data.contact_person,
                isCarrier: data.is_carrier,
                isOutlet: data.is_outlet,
                isSupplier: data.is_supplier,
                isBillTo: data.is_bill_to,
                notes: data.notes,
                isActive: data.is_active,
                createdAt: data.created_at,
                updatedAt: data.updated_at,
                accountId: data.account_id,
                alias: data.alias,
            };
        }

        // Normal company update
        const now = new Date().toISOString();

        const updateData: any = {
            updated_at: now,
        };

        if (updates.name !== undefined) updateData.name = updates.name;
        if (updates.address !== undefined) updateData.address = updates.address;
        if (updates.city !== undefined) updateData.city = updates.city;
        if (updates.state !== undefined) updateData.state = updates.state;
        if (updates.zipCode !== undefined) updateData.zip_code = updates.zipCode;
        if (updates.phone !== undefined) updateData.phone = updates.phone;
        if (updates.email !== undefined) updateData.email = updates.email;
        if (updates.contactPerson !== undefined) updateData.contact_person = updates.contactPerson;
        if (updates.isCarrier !== undefined) updateData.is_carrier = updates.isCarrier;
        if (updates.isOutlet !== undefined) updateData.is_outlet = updates.isOutlet;
        if (updates.isSupplier !== undefined) updateData.is_supplier = updates.isSupplier;
        if (updates.isBillTo !== undefined) updateData.is_bill_to = updates.isBillTo;
        if (updates.notes !== undefined) updateData.notes = updates.notes;
        if (updates.isActive !== undefined) updateData.is_active = updates.isActive;
        if (updates.alias !== undefined) updateData.alias = updates.alias;

        const { data, error } = await supabase
            .from('companies')
            .update(updateData)
            .eq('id', id)
            .select()
            .single();

        if (error) {
            throw new Error(error.message);
        }

        return {
            id: data.id,
            name: data.name,
            address: data.address,
            city: data.city,
            state: data.state,
            zipCode: data.zip_code,
            phone: data.phone,
            email: data.email,
            contactPerson: data.contact_person,
            isCarrier: data.is_carrier,
            isOutlet: data.is_outlet,
            isSupplier: data.is_supplier,
            isBillTo: data.is_bill_to,
            notes: data.notes,
            isActive: data.is_active,
            createdAt: data.created_at,
            updatedAt: data.updated_at,
            accountId: data.account_id,
        };
    } catch (error: any) {
        throw new Error(error.message || 'Failed to update company');
    }
}

export async function deleteCompany(
    id: string,
    impersonationData?: {
        isImpersonating: boolean;
        impersonatedUserId: string;
        accountId: string;
    }
): Promise<void> {
    try {
        if (impersonationData?.isImpersonating) {
            // Use impersonation endpoint for company deletion
            const { data: { user: currentUser } } = await supabase.auth.getUser();
            const token = (await supabase.auth.getSession()).data.session?.access_token;

            const response = await fetch(`${API_BASE_URL}/admin/impersonate/${impersonationData.impersonatedUserId}/account/${impersonationData.accountId}/companies/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ currentUserId: currentUser?.id }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to delete company for impersonated user');
            }
        } else {
            // Normal soft delete by marking as inactive
            const { error } = await supabase
                .from('companies')
                .update({
                    is_active: false,
                    updated_at: new Date().toISOString()
                })
                .eq('id', id);

            if (error) {
                throw new Error(error.message);
            }
        }
    } catch (error: any) {
        throw new Error(error.message || 'Failed to delete company');
    }
}

export async function checkCompanyNameExists(name: string): Promise<{ exists: boolean; isActive: boolean }> {
    try {
        const { data, error } = await supabase
            .from('companies')
            .select('is_active')
            .eq('name', name)
            .single();

        if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
            throw new Error(error.message);
        }

        if (!data) {
            return { exists: false, isActive: false };
        }

        return { exists: true, isActive: data.is_active };
    } catch (error: any) {
        throw new Error(error.message || 'Failed to check company name');
    }
} 